"use strict";
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const _ = __importStar(require("lodash"));
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const util_1 = require("./util");
exports.IDENTITY = (t) => t;
exports.PASSTHRU = (t) => rxjs_1.of(t);
// --------------------------------------------------------------------
// Operators
function tapWithIndex(action) {
    return operators_1.map((t, index) => {
        action(t, index);
        return t;
    });
}
exports.tapWithIndex = tapWithIndex;
function tapScan(seed, f) {
    return (source) => source.pipe(scan2({ acc: seed }, (acc, t, index) => {
        const inner = f(acc.acc, t, index);
        return { acc: inner, t };
    }), operators_1.map(({ t }) => t));
}
exports.tapScan = tapScan;
function lastOrEmpty() {
    return (source) => source.pipe(operators_1.last(), operators_1.catchError(err => rxjs_1.EMPTY));
}
exports.lastOrEmpty = lastOrEmpty;
function filterFirst() {
    return operators_1.filter((value, index) => index === 0);
}
exports.filterFirst = filterFirst;
function takeDuring(control$) {
    const stop$ = control$.pipe(operators_1.last(_ => false, true));
    return operators_1.takeUntil(stop$);
}
exports.takeDuring = takeDuring;
function postponeUntil(signal) {
    return (source) => rxjs_1.zip(source, signal).pipe(operators_1.first(), operators_1.map(([t, s]) => t));
}
exports.postponeUntil = postponeUntil;
function filterInstanceOf(klass) {
    return (s) => s.pipe(operators_1.filter(x => x instanceof klass));
}
exports.filterInstanceOf = filterInstanceOf;
function filterIs(f) {
    return operators_1.filter(f);
}
exports.filterIs = filterIs;
function filterDefined() {
    return (s) => s.pipe(operators_1.filter((x) => x !== null && x !== undefined));
}
exports.filterDefined = filterDefined;
function flipBoolean() {
    return operators_1.map(b => !b);
}
exports.flipBoolean = flipBoolean;
// Delays passing on the Observable until the predicate Observable issues a single
// true or false.
// If predicate completes without issuing a true, then nothing gets passed.
//
function filterObservable(predicate) {
    return (source) => rxjs_1.zip(source, source.pipe(operators_1.flatMap(s => predicate(s).pipe(operators_1.first(_ => true, false)
    // WARNING: `first` is different in RxJS that in RxJava!
    ))))
        .pipe(operators_1.filter(([orig, bool]) => bool), operators_1.map(([orig, bool]) => orig));
}
exports.filterObservable = filterObservable;
function detour(selector, observableTrue = exports.PASSTHRU, observableFalse = exports.PASSTHRU) {
    return operators_1.flatMap((t) => selector(t) ? observableTrue(t) : observableFalse(t));
}
exports.detour = detour;
function interject(f) {
    return operators_1.concatMap((t) => rxjs_1.concat(f(t).pipe(operators_1.ignoreElements()), rxjs_1.of(t)));
}
exports.interject = interject;
//----------------------------------------------------------------
// Delays the completion by t
//
//   ------1-------------2-----3----|
//
// will output
//
//   ------1-------------2-----3----==========|
//
//
function extend(t, scheduler = rxjs_1.asyncScheduler) {
    if (t <= 0)
        return (x) => x;
    return (source) => {
        return rxjs_1.concat(source, rxjs_1.defer(() => rxjs_1.timer(t).pipe(operators_1.ignoreElements())));
    };
}
exports.extend = extend;
// Redefine the RxJS `scan` so that we can have a separate initial SEED type.
// Note also the argument order is reversed.
//
function scan2(seed, f) {
    return operators_1.scan(f, seed);
    // OK to case because since `f` ONLY returns R, the whole thing can only
    // return R!
}
exports.scan2 = scan2;
// `map` that also removes null|undefined from the output.
function filtering(f) {
    return (source) => source.pipe(operators_1.map(f), operators_1.filter(r => !!r)); // We are filtering the undefined|null
}
exports.filtering = filtering;
function finding(f) {
    return filtering((a) => a.find(f));
}
exports.finding = finding;
// ----------------------------------------------------------------
// Given input
//
//   ------1-------------2--------------------3------------------>
//
// will output
//
//   ------1=================|2======|--------3========|--------->
//
// where the length of each is calculated by the given function.
//
function enqueue(durationF, scheduler = rxjs_1.asyncScheduler) {
    return (source) => {
        return source.pipe(operators_1.concatMap((x) => rxjs_1.of(x).pipe(extend(durationF(x)))));
    };
}
exports.enqueue = enqueue;
// ----------------------------------------------------------------
// Accumulates the incoming values to be "active" for a fixed time.
// Outputs the current values as they become active or inactive.
//
//   ------1-------------2-----3------|
//
// will output
//
//   ------[1]=======[]--[2]===[2,3]=[3]=====|
//
//
function prolong(t, scheduler = rxjs_1.asyncScheduler) {
    return (source) => {
        const add$ = source.pipe(operators_1.map(t => [true, t]));
        const del$ = source.pipe(operators_1.delay(t, scheduler), operators_1.map(t => [false, t]));
        return rxjs_1.merge(add$, del$).pipe(operators_1.scan((acc, [op, t]) => op ? [...acc, t] : util_1.withoutFirst(acc, t), []));
    };
}
exports.prolong = prolong;
function doOnSubscribe(f) {
    return (source) => {
        return rxjs_1.defer(() => {
            f();
            return source;
        });
    };
}
exports.doOnSubscribe = doOnSubscribe;
// ---------------------------------------------------------------------------
function cachedMapper(keyF, mapF) {
    const cache = new Map();
    return function (tFrom) {
        const key = keyF(tFrom);
        const prev = cache.get(key);
        if (prev) {
            return prev;
        }
        // else
        const output = mapF(tFrom);
        cache.set(key, output);
        return output;
    };
}
exports.cachedMapper = cachedMapper;
// ----------------------------------------------------------------
//
function zipEmptiable(...observables) {
    if (observables.length === 0) {
        return rxjs_1.of([]);
    }
    return rxjs_1.zip(...observables);
}
exports.zipEmptiable = zipEmptiable;
// ----------------------------------------------------------------------------
function pairFirst() {
    return (source) => rxjs_1.combineLatest([
        source.pipe(operators_1.take(1)),
        source.pipe(operators_1.skip(1)),
    ]).pipe();
}
exports.pairFirst = pairFirst;
// ----------------------------------------------------------------------------
function combineLatestWithEmpty(arr) {
    return arr.length ? rxjs_1.combineLatest(arr) : rxjs_1.of([]); // used instead of EMPTY so the Observable does not complete()
}
exports.combineLatestWithEmpty = combineLatestWithEmpty;
function combineLatestWithChanges(arr) {
    var version = 0;
    const arrV$ = arr.map(s => s.pipe(operators_1.map(e => [e, ++version])));
    const x$ = combineLatestWithEmpty(arrV$).pipe(operators_1.map((arrV) => [
        arrV.map(([t, number]) => t),
        arrV.length ? [_.maxBy(arrV, ([t, n]) => n)[0]] : []
    ]));
    return x$;
}
exports.combineLatestWithChanges = combineLatestWithChanges;
// ----------------------------------------------------------------------------
// Promise utility
function promise$(f) {
    return rxjs_1.defer(() => rxjs_1.from(f()));
}
exports.promise$ = promise$;
// ----------------------------------------------------------------
// Utility methods
function finding$(a$, f) {
    return a$.pipe(finding(f));
}
exports.finding$ = finding$;
// =======================================================================
function swapMap(f) {
    let subsCur;
    return source => {
        const input$ = source.pipe(operators_1.map(f));
        return new rxjs_1.Observable(subsOut => {
            const subsOuter = input$.subscribe(in$ => {
                // ---- Subscribe to the NEW observable,
                //      and THEN unsubscribe to the previous one
                const subsNew = in$.subscribe(t => subsOut.next(t), error => subsOut.error(error), () => { if (subsOuter.closed)
                    subsOut.complete(); });
                if (subsCur)
                    subsCur.unsubscribe();
                subsCur = subsNew;
            }, error => subsOut.error(error), () => { if (!subsCur)
                subsOut.complete(); });
            return () => {
                if (subsCur) {
                    subsCur.unsubscribe();
                    subsCur = undefined;
                }
                subsOuter.unsubscribe();
            };
        });
    };
}
exports.swapMap = swapMap;
// --------------------------------------------------------------------
// Flattening
function flattenObject(f) {
    return rxjs_1.pipe(operators_1.map(util_1.objectToArray), operators_1.map(a => a.map(([k, v]) => f(v).pipe(operators_1.map(tOut => [k, tOut])))), operators_1.flatMap(obs => rxjs_1.combineLatest(obs)), operators_1.map(util_1.objectFromArray));
}
exports.flattenObject = flattenObject;
function flattenArray(f) {
    return (source) => source.pipe(operators_1.switchMap((ts) => ts.length > 0 ? rxjs_1.combineLatest(ts.map(t => f(t))) : rxjs_1.of([])));
}
exports.flattenArray = flattenArray;
function repeating(f, dueTime = 0, periodOrScheduler, scheduler) {
    return rxjs_1.timer(dueTime, periodOrScheduler, scheduler).pipe(operators_1.map(n => (f instanceof Function ? f(n) : f)));
}
exports.repeating = repeating;
function nextIfNotEqual(value, dest) {
    if (!_.isEqual(value, dest.value))
        dest.next(value);
}
exports.nextIfNotEqual = nextIfNotEqual;
function progressFork(f, status = () => null) {
    const p = new rxjs_1.BehaviorSubject(null);
    return (source) => source.pipe(doOnSubscribe(() => f(p)), operators_1.tap(t => p.next(status(t))), operators_1.finalize(() => p.complete()));
}
exports.progressFork = progressFork;
function progressCount() {
    return (source) => source.pipe(operators_1.mergeMap(p => p.pipe(operators_1.ignoreElements(), operators_1.startWith(1), operators_1.endWith(-1))), scan2(0, (count, n) => count + n), operators_1.startWith(0));
}
exports.progressCount = progressCount;
